import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Injectable } from '@angular/core';
import { CapacitorHttp } from '@capacitor/core';
import { from, throwError, Observable, of } from 'rxjs';
import { HttpResponse, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { map, catchError, switchMap, finalize, tap } from 'rxjs/operators';
import * as i1 from '@firestitch/store';
import { parse } from '@firestitch/date';
import { isAfter, isBefore } from 'date-fns';
import setCookie from 'set-cookie-parser';
import * as i1$1 from '@ionic/angular';
import { FileClickInterceptor } from '@firestitch/file';

const FS_CORDOVA_CONFIG = new InjectionToken('fs-cordova-config');

class FsCordovaModule {
    static forRoot(config = {}) {
        return {
            ngModule: FsCordovaModule,
            providers: [
                { provide: FS_CORDOVA_CONFIG, useValue: config },
            ],
        };
    }
}
FsCordovaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsCordovaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaModule });
FsCordovaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaModule, decorators: [{
            type: NgModule
        }] });

var CordovaState;
(function (CordovaState) {
    CordovaState["Pending"] = "pending";
    CordovaState["Ready"] = "ready";
    CordovaState["Unsupported"] = "unsupported";
})(CordovaState || (CordovaState = {}));

const cordovaSupported = document.location.port === '8080' ||
    (document.location.host === 'localhost' && !document.location.port) ||
    document.location.protocol === 'file:' ||
    document.location.protocol === 'ionic:' ||
    document.location.protocol === 'httpsionic:' ||
    document.location.protocol === 'capacitor:' ||
    document.location.protocol === 'app:';

function initCordova() {
    window.addEventListener = function () {
        EventTarget.prototype.addEventListener.apply(this, arguments);
    };
    window.removeEventListener = function () {
        EventTarget.prototype.removeEventListener.apply(this, arguments);
    };
    document.addEventListener = function () {
        EventTarget.prototype.addEventListener.apply(this, arguments);
    };
    document.removeEventListener = function () {
        EventTarget.prototype.removeEventListener.apply(this, arguments);
    };
    if (cordovaSupported) {
        window.NativeFile = window.File;
        window.NativeFileReader = window.FileReader;
        window.cordovaState = CordovaState.Pending;
        const script = document.createElement('script');
        script.src = 'cordova.js';
        script.onload = () => {
            window.dispatchEvent(new CustomEvent('cordovaLoaded', {
                detail: {},
                bubbles: true,
                cancelable: true,
                composed: false,
            }));
        };
        const head = document.getElementsByTagName('head')[0];
        head.appendChild(script);
        const base = document.getElementsByTagName('base')[0];
        base.setAttribute('href', '.');
    }
    else {
        window.cordovaState = CordovaState.Unsupported;
    }
}

function getCordova() {
    return window.cordova;
}

function getCordovaCamera() {
    return navigator.camera;
}

function getCordovaCapture() {
    const device = navigator.device;
    return device ? device.capture : null;
}

function getCordovaResolveLocalFileSystemURL() {
    return window.resolveLocalFileSystemURL;
}

function hasCordovaCameraSupport() {
    return getCordovaCamera() && getCordovaResolveLocalFileSystemURL();
}

function hasCordovaCaptureSupport() {
    return getCordovaCapture() && getCordovaResolveLocalFileSystemURL();
}

class FsCapacitorHttp {
    sendRequest(request) {
        const headers = request.headers.keys()
            .filter((name) => typeof request.headers.get(name) === 'string')
            .reduce((accum, name) => {
            return {
                ...accum,
                [name]: request.headers.get(name),
            };
        }, {});
        const params = request.params.keys()
            .reduce((accum, name) => {
            return {
                ...accum,
                [name]: request.params.get(name),
            };
        }, {});
        const serializer = this._getSerializer(request);
        // if(request.body instanceof FormData) {
        //   const formData = new FormData();
        //   request.body
        //     .forEach((value, name) => {
        //       formData.append(name, value);
        //     });
        //   request = request.clone({ body: formData });
        // }
        let data = request.body || '';
        if (serializer === 'json') {
            data = data || {};
        }
        return this._sendRequest(request.url, {
            method: request.method.toUpperCase(),
            data,
            params,
            headers: {
                ...headers,
                ['Cookie']: document.cookie,
            },
            serializer,
        });
    }
    _sendRequest(url, options) {
        const httpOptions = {
            method: options.method,
            url,
            data: options.data,
            params: options.params,
            headers: options.headers,
        };
        return from(CapacitorHttp.request(httpOptions))
            .pipe(map((response) => {
            let body;
            try {
                body = JSON.parse(response.data);
            }
            catch (error) {
                body = response.data;
            }
            const httpResponse = new HttpResponse({
                body,
                status: response.status,
                headers: new HttpHeaders(response.headers),
                url: response.url,
            });
            this._log(options, body, httpResponse);
            document.cookie = response.headers['set-cookie'];
            return httpResponse;
        }), catchError((error) => {
            if (error.status <= 0) {
                const errorResponse = new HttpErrorResponse({
                    ...error,
                    url,
                });
                this._log(options, '', errorResponse);
            }
            else {
                let body = error.error;
                try {
                    body = JSON.parse(error.error);
                }
                catch (e) { }
                const httpResponse = new HttpResponse({
                    body,
                    status: error.status,
                    headers: new HttpHeaders(error.headers),
                    url: error.url,
                });
                if (!httpResponse.status || httpResponse.status >= 400) {
                    const errorResponse = new HttpErrorResponse({
                        error: httpResponse.body,
                        headers: httpResponse.headers,
                        status: httpResponse.status,
                        statusText: httpResponse.statusText,
                        url: httpResponse.url,
                    });
                    this._log(options, httpResponse.body, errorResponse);
                }
                this._log(options, httpResponse.body, httpResponse);
            }
            return throwError(error);
        }));
    }
    _log(options, body, httpResponse) {
        const _url = new URL(httpResponse.url);
        Object.keys(options.params)
            .forEach((name) => {
            _url.searchParams.set(name, options.params[name]);
        });
        const status = httpResponse?.status || 0;
        const log = [`${options.method.toUpperCase()} ${status}`, _url.toString(), options.data || ''];
        if (httpResponse) {
            log.push(...[
                status,
                options.headers,
                body || '',
                httpResponse.headers.keys()
                    .filter((name) => typeof httpResponse.headers.get(name) === 'string')
                    .reduce((accum, name) => {
                    return {
                        ...accum,
                        [name]: httpResponse.headers.get(name),
                    };
                }, {}),
            ]);
        }
        if (!status || status >= 400) {
            console.error(...log);
        }
        else {
            console.log(...log);
        }
    }
    _getSerializer(request) {
        if (request.body instanceof FormData) {
            return 'multipart';
        }
        if (typeof (request.body) === 'object') {
            return 'json';
        }
        return 'utf8';
    }
}
FsCapacitorHttp.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCapacitorHttp, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FsCapacitorHttp.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCapacitorHttp, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCapacitorHttp, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class CordovaCameraFileService {
    constructor() {
        this.cordova = {
            camera: null,
            capture: null,
            resolveLocalFileSystemURL: null
        };
        this.cordova.camera = getCordovaCamera();
        this.cordova.capture = getCordovaCapture();
        this.cordova.resolveLocalFileSystemURL = getCordovaResolveLocalFileSystemURL();
    }
    cordovaCameraCleanup() {
        this.cordova.camera.cleanup();
    }
    selectCordovaCameraPicture() {
        const options = {
            destinationType: this.cordova.camera.DestinationType.FILE_URI,
            encodingType: this.cordova.camera.EncodingType.JPEG,
            sourceType: this.cordova.camera.PictureSourceType.CAMERA,
            mediaType: this.cordova.camera.MediaType.PICTURE,
            quality: 100,
            correctOrientation: true,
        };
        return new Observable((observer) => {
            this.cordova.camera.getPicture((data) => {
                observer.next(data);
                observer.complete();
            }, (error) => {
                observer.error(error);
            }, options);
        })
            .pipe(switchMap((path) => this._getFile(path)), finalize(() => this.cordovaCameraCleanup()));
    }
    _getFile(path) {
        if (!path.match(/^file:/)) {
            path = 'file://'.concat(path);
        }
        return new Observable((observer) => {
            this.cordova.resolveLocalFileSystemURL(path, (fileEntry) => {
                observer.next(fileEntry);
                observer.complete();
            }, (error) => {
                observer.error(error);
            });
        })
            .pipe(switchMap((fileEntry) => {
            return new Observable((fileEntryObserver) => {
                fileEntry.file((file) => {
                    const reader = new window.CordovaFileReader();
                    reader.onloadend = (encodedFile) => {
                        const fileData = encodedFile.target.result.split('base64,').pop();
                        const byteString = atob(fileData);
                        let n = byteString.length;
                        const u8arr = new Uint8Array(n);
                        while (n--) {
                            u8arr[n] = byteString.charCodeAt(n);
                        }
                        const blob = new File([u8arr], file.name, { type: file.type });
                        fileEntryObserver.next(blob);
                        fileEntryObserver.complete();
                    };
                    reader.onerror = (error) => {
                        fileEntryObserver.error(error);
                    };
                    reader.readAsDataURL(file);
                }, (error) => {
                    fileEntryObserver.error(error);
                });
            });
        }));
    }
}

class FsCordovaCookie {
    constructor(_store) {
        this._store = _store;
        this._cookies = [];
    }
    init() {
        this._cookies = (this._store.get('cookie') || [])
            .map((cookie) => ({
            ...cookie,
            expires: parse(cookie.expires),
        }));
        this.polyfillCookie();
    }
    parseCookies(cookieStr) {
        return setCookie.parse(setCookie.splitCookiesString(cookieStr));
    }
    polyfillCookie() {
        Object.defineProperty(document, 'cookie', {
            get: () => {
                return this._cookies
                    .filter((cookie) => {
                    return isAfter(cookie.expires, new Date());
                })
                    .map((cookie) => {
                    return `${cookie.name}=${cookie.value}`;
                })
                    .join('; ');
            },
            set: (cookieStr) => {
                const cookies = this.parseCookies(cookieStr);
                cookies.forEach((cookie) => {
                    const expired = isBefore(cookie.expires || 0, new Date());
                    this._cookies = this._cookies
                        .filter((item) => {
                        return item.name !== cookie.name;
                    });
                    if (!expired) {
                        this._cookies.push(cookie);
                    }
                });
                this._store.set('cookie', this._cookies);
            },
        });
    }
}
FsCordovaCookie.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaCookie, deps: [{ token: i1.FsStore }], target: i0.ɵɵFactoryTarget.Injectable });
FsCordovaCookie.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaCookie, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaCookie, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.FsStore }]; } });

class FsCordovaHttp {
    get http() {
        const cordova = window.cordova;
        if (!cordova?.plugin?.http) {
            throw new Error('Plugin cordova-plugin-advanced-http not installed');
        }
        return cordova.plugin.http;
    }
    sendRequest(request) {
        const headers = request.headers.keys()
            .filter((name) => typeof request.headers.get(name) === 'string')
            .reduce((accum, name) => {
            return {
                ...accum,
                [name]: request.headers.get(name),
            };
        }, {});
        const params = request.params.keys()
            .reduce((accum, name) => {
            return {
                ...accum,
                [name]: request.params.get(name),
            };
        }, {});
        const serializer = this._getSerializer(request);
        if (request.body instanceof FormData) {
            const formData = new this.http.ponyfills.FormData();
            request.body
                .forEach((value, name) => {
                formData.append(name, value);
            });
            request = request.clone({ body: formData });
        }
        let data = request.body || '';
        if (serializer === 'json') {
            data = data || {};
        }
        return this._sendRequest(request.url, {
            method: request.method.toLowerCase(),
            data,
            params,
            headers: {
                ...headers,
                ['Cookie']: document.cookie,
            },
            serializer,
        });
    }
    _sendRequest(url, options) {
        return new Observable((observer) => {
            this.http.sendRequest(url, options, (response) => {
                let body;
                try {
                    body = JSON.parse(response.data);
                }
                catch (error) {
                    body = response.data;
                }
                const httpResponse = new HttpResponse({
                    body,
                    status: response.status,
                    headers: new HttpHeaders(response.headers),
                    url: response.url,
                });
                this._log(options, body, httpResponse);
                document.cookie = response.headers['set-cookie'];
                observer.next(httpResponse);
                observer.complete();
            }, (error) => {
                if (error.status <= 0) {
                    const errorResponse = new HttpErrorResponse({
                        ...error,
                        url,
                    });
                    this._log(options, '', errorResponse);
                    return observer.error();
                }
                let body = error.error;
                try {
                    body = JSON.parse(error.error);
                }
                catch (e) { }
                const httpResponse = new HttpResponse({
                    body,
                    status: error.status,
                    headers: new HttpHeaders(error.headers),
                    url: error.url,
                });
                if (!httpResponse.status || httpResponse.status >= 400) {
                    const errorResponse = new HttpErrorResponse({
                        error: httpResponse.body,
                        headers: httpResponse.headers,
                        status: httpResponse.status,
                        statusText: httpResponse.statusText,
                        url: httpResponse.url,
                    });
                    this._log(options, httpResponse.body, errorResponse);
                    return observer.error(errorResponse);
                }
                this._log(options, httpResponse.body, httpResponse);
                observer.next(httpResponse);
                observer.complete();
            });
        });
    }
    _log(options, body, httpResponse) {
        const _url = new URL(httpResponse.url);
        Object.keys(options.params)
            .forEach((name) => {
            _url.searchParams.set(name, options.params[name]);
        });
        const status = httpResponse?.status || 0;
        const log = [`${options.method.toUpperCase()} ${status}`, _url.toString(), options.data || ''];
        if (httpResponse) {
            log.push(...[
                status,
                options.headers,
                body || '',
                httpResponse.headers.keys()
                    .filter((name) => typeof httpResponse.headers.get(name) === 'string')
                    .reduce((accum, name) => {
                    return {
                        ...accum,
                        [name]: httpResponse.headers.get(name),
                    };
                }, {}),
            ]);
        }
        if (!status || status >= 400) {
            console.error(...log);
        }
        else {
            console.log(...log);
        }
    }
    _getSerializer(request) {
        if (request.body instanceof FormData) {
            return 'multipart';
        }
        if (typeof (request.body) === 'object') {
            return 'json';
        }
        return 'utf8';
    }
}
FsCordovaHttp.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaHttp, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FsCordovaHttp.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaHttp, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordovaHttp, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class FsCordova {
    constructor(_platform, _cordovaCookie) {
        this._platform = _platform;
        this._cordovaCookie = _cordovaCookie;
        this._ready = false;
    }
    get ready$() {
        if (this._ready) {
            return of(true);
        }
        return from(this._platform.ready())
            .pipe(switchMap(() => this._cordovaReady()), tap(() => {
            this._ready = true;
        }));
    }
    get resume$() {
        return this._platform.resume;
    }
    get window() {
        return window;
    }
    get state() {
        return this.window.cordovaState;
    }
    set state(value) {
        this.window.cordovaState = value;
    }
    get cordova() {
        return this.window.cordova;
    }
    get ready() {
        return this.state === CordovaState.Ready;
    }
    get unsupported() {
        return this.state === CordovaState.Unsupported;
    }
    _cordovaReady() {
        if (this.state === CordovaState.Unsupported) {
            return throwError('Cordova not supported');
        }
        if (this.state === CordovaState.Ready) {
            return of(null);
        }
        return new Observable((observer) => {
            if (this.cordova) {
                observer.next(this.cordova);
                observer.complete();
                return;
            }
            this.window.addEventListener('cordovaLoaded', () => {
                observer.next(this.cordova);
                observer.complete();
            });
        })
            .pipe(switchMap((cordova) => new Observable((observer) => {
            const channel = cordova.require('cordova/channel');
            channel.onCordovaReady
                .subscribe(() => {
                observer.next();
                observer.complete();
            });
        })), tap(() => {
            this.state = CordovaState.Ready;
        }));
    }
    getAppVersion() {
        if (!getCordova()?.getAppVersion) {
            return of(null);
        }
        return from(getCordova().getAppVersion.getVersionNumber());
    }
    init() {
        return this.ready$
            .pipe(tap(() => {
            console.log('Cordova Service init() ready');
        }), tap(() => this._initFile()), tap(() => this._cordovaCookie.init()), tap(() => this._initInsets()));
    }
    _initInsets() {
        if (this.window.totalpave) {
            this.window.totalpave.Insets.addListener((insets) => {
                const root = document.querySelector(':root');
                root.style.setProperty('--safe-area-inset-top', `${insets.top}px`);
                root.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
                root.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
                root.style.setProperty('--safe-area-inset-left', `${insets.left}px`);
            });
        }
    }
    /**
     * Restored the File/FileReader object from cordova-plugin-file overriding it
     */
    _initFile() {
        this.CordovaFile = this.window.File;
        this.CordovaFileReader = this.window.FileReader;
        this.window.File = window.NativeFile;
        this.window.FileReader = window.NativeFileReader;
        this.window.CordovaFile = this.CordovaFile;
        this.window.CordovaFileReader = this.CordovaFileReader;
    }
}
FsCordova.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordova, deps: [{ token: i1$1.Platform }, { token: FsCordovaCookie }], target: i0.ɵɵFactoryTarget.Injectable });
FsCordova.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordova, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsCordova, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Platform }, { type: FsCordovaCookie }]; } });

class CapacitorHttpInterceptor {
    constructor(_cordova, _cordovaHttp) {
        this._cordova = _cordova;
        this._cordovaHttp = _cordovaHttp;
    }
    intercept(request, next) {
        return this._cordova.ready && request.url.match(/^http/) ? this._cordovaHttp.sendRequest(request) : next.handle(request);
    }
}
CapacitorHttpInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CapacitorHttpInterceptor, deps: [{ token: FsCordova }, { token: FsCordovaHttp }], target: i0.ɵɵFactoryTarget.Injectable });
CapacitorHttpInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CapacitorHttpInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CapacitorHttpInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FsCordova }, { type: FsCordovaHttp }]; } });

class CordovaFileClickInterceptor extends FileClickInterceptor {
    constructor(_ngZone) {
        super();
        this._ngZone = _ngZone;
    }
    intercept(event, inputProcessorService, next) {
        if (hasCordovaCameraSupport()) {
            // if(inputProcessorService.capture === 'library') {
            //   this._cordovaCameraFileService.selectCordovaCameraLibrary();  
            //   return of(null);      
            // } 
            if (inputProcessorService.capture === 'camera' && inputProcessorService.isAcceptImage()) {
                return (new CordovaCameraFileService())
                    .selectCordovaCameraPicture()
                    .pipe(tap((file) => {
                    this._ngZone.run(() => {
                        inputProcessorService.selectFiles([file]);
                    });
                }));
            }
        }
        return next.handle(event, inputProcessorService);
    }
}

class CordovaHttpInterceptor {
    constructor(_cordova, _cordovaHttp) {
        this._cordova = _cordova;
        this._cordovaHttp = _cordovaHttp;
    }
    intercept(request, next) {
        return this._cordova.ready && request.url.match(/^http/) ? this._cordovaHttp.sendRequest(request) : next.handle(request);
    }
}
CordovaHttpInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CordovaHttpInterceptor, deps: [{ token: FsCordova }, { token: FsCordovaHttp }], target: i0.ɵɵFactoryTarget.Injectable });
CordovaHttpInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CordovaHttpInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: CordovaHttpInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FsCordova }, { type: FsCordovaHttp }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { CapacitorHttpInterceptor, CordovaCameraFileService, CordovaFileClickInterceptor, CordovaHttpInterceptor, FS_CORDOVA_CONFIG, FsCapacitorHttp, FsCordova, FsCordovaCookie, FsCordovaHttp, FsCordovaModule, cordovaSupported, getCordova, getCordovaCamera, getCordovaCapture, getCordovaResolveLocalFileSystemURL, hasCordovaCameraSupport, hasCordovaCaptureSupport, initCordova };
